import moment from 'moment';
import {
  BRAND,
  REQUEST_VERSION,
  WireChannel,
  WirePortfolio,
  WireProduct,
  WireStatus,
  WireTransactionType,
} from 'scenes/common/api/quote/types';
import { ApiState } from 'scenes/common/helpers/apiData/types';
import { toQuoteNumberWithDashes } from 'scenes/common/helpers/quote';
import {
  CardTypes,
  InsuranceName,
  PaymentFrequency,
  PaymentWhen,
} from 'scenes/common/types';
import {
  AddressesEntity,
  CoverType,
  PaymentState,
  Policy,
  PolicyPayload,
  RenewalStatus,
  WireOtherDetailsRequest,
  WireOtherDetailsResponse,
  WirePolicyRetrieveResponse,
} from 'scenes/payments/types';

const getPolicyType = (quoteNumber: string) => {
  const prefix = quoteNumber.substr(0, 4);
  switch (prefix) {
    default:
    case 'COMO':
      return WireProduct.Motor;
    case 'COHO':
      return WireProduct.Home;
    case 'COLA':
      return WireProduct.Landlord;
  }
};

export const getPayload = (quoteNumber: string, postcode: string) => ({
  brand: BRAND,
  product: getPolicyType(quoteNumber),
  portfolio: WirePortfolio.ColesCp,
  channel: WireChannel.Web,
  version: REQUEST_VERSION,
  targetStatus: WireStatus.RetrievePayment,
  transactionType: WireTransactionType.Policy,
  policyNumber: toQuoteNumberWithDashes(quoteNumber),
  postcode,
});

const toCoverType = (coverType: string): CoverType => coverType as CoverType;

const toWiredStatus = (status: string): WireStatus => status as WireStatus;

const toWireOtherDetails = (
  otherDetails: WireOtherDetailsResponse
): WireOtherDetailsResponse => ({ ...otherDetails });

const toAddress = (addresses: AddressesEntity[] | null | undefined) =>
  addresses?.find((address) => address.id.toLowerCase() === 'addr-0');

const toRenewalStatus = (renewalStatus: string): RenewalStatus => {
  switch (renewalStatus) {
    case 'Invited':
    case 'Reminder':
    case 'PreRenewal':
      return RenewalStatus.Renewal;
    case 'Dishonour':
    default:
      return RenewalStatus.Dishonour;
  }
};

const toWiredPolicyResponse = (
  policy: Policy,
  postcode: string
): WirePolicyRetrieveResponse => ({
  number: policy.number,
  reference: policy.reference,
  status: toWiredStatus(policy.status),
  dates: policy.dates,
  renewalStatus: toRenewalStatus(policy.renewalStatus),
  otherDetails: toWireOtherDetails(policy.otherDetails),
  postcode,
});

export const mapToPaymentState = (
  payload: PolicyPayload,
  postcode: string
): Partial<PaymentState> => {
  const { policy, coverType, addresses } = payload;
  return {
    product: getPolicyType(payload?.policy.number),
    premiums: payload.premiums,
    address: toAddress(addresses),
    coverType: toCoverType(coverType),
    policy: toWiredPolicyResponse(policy, postcode),
    loadingStatus: ApiState.Loaded,
  };
};

export const toInsuranceName = (product: WireProduct): InsuranceName => {
  switch (product) {
    case WireProduct.Motor:
      return InsuranceName.Car;
    case WireProduct.Home:
      return InsuranceName.Home;
    case WireProduct.Landlord:
      return InsuranceName.Landlord;

    default:
      return InsuranceName.Car;
  }
};

export const toPolicyNumber = (policy: WirePolicyRetrieveResponse) =>
  policy.number;

export const toPaymentDetails = (
  state: PaymentState,
  cardType: CardTypes = CardTypes.Unspecified
) => {
  const { payment } = state;
  const effective = state.policy.dates?.effective;

  if (payment === undefined) {
    throw new Error('payment is empty');
  }

  let paymentDate = moment().format('YYYY-MM-DD');

  if (
    payment.paymentWhen === PaymentWhen.DueDate &&
    effective &&
    moment(effective).isSameOrAfter(moment())
  ) {
    paymentDate = effective;
  }

  return {
    paymentFrequency: PaymentFrequency.Annually,
    paymentDate,
    paymentMethod: payment.paymentMethod,
    paymentCardType: cardType,
  };
};

export const toOtherDetails = (
  paymentState: PaymentState
): WireOtherDetailsRequest => {
  const { description, risk, office } = { ...paymentState.policy.otherDetails };
  const email = paymentState.payment?.contactEmailAddress ?? undefined;

  return {
    description,
    risk,
    office,
    email,
  };
};
